import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { getActiveOrders, updateOrder } from '../api/restaurant';
import { ChefHat, Clock, ArrowLeft, Check, ArrowRight, UtensilsCrossed, Wine, Undo2, Beer } from 'lucide-react';
import api from '../api/axios';

const STATUSES = ['En attente', 'En préparation', 'Prête', 'Servie'];

const KitchenDisplay = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('cuisine'); // 'cuisine' | 'boissons' | 'all'
  const staffRole = localStorage.getItem('staffRole') || '';

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    if (!token) { navigate('/staff/login'); return; }
    fetchOrders();
    const interval = setInterval(fetchOrders, 8000);
    return () => clearInterval(interval);
  }, [navigate]);

  const fetchOrders = async () => {
    try { const data = await getActiveOrders(); setOrders(data); }
    catch { toast({ title: 'Erreur', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const handleStatusChange = async (id, status) => {
    try { await updateOrder(id, { status }); await fetchOrders(); toast({ title: `Statut: ${status}` }); }
    catch { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  const handleItemStatusChange = async (orderId, itemIndex, newStatus) => {
    try {
      await api.put(`/orders/${orderId}/items/${itemIndex}/status`, { status: newStatus });
      await fetchOrders();
    } catch { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  const getAvailableStatuses = (currentStatus) => {
    const idx = STATUSES.indexOf(currentStatus);
    if (idx === -1) return [];
    const priv = staffRole === 'admin' || staffRole === 'webmaster' || staffRole === 'cuisinier';
    if (priv) return STATUSES.filter(s => s !== currentStatus);
    return idx < STATUSES.length - 1 ? [STATUSES[idx + 1]] : [];
  };

  // Group orders by table
  const groupedByTable = {};
  orders.forEach(o => {
    const k = o.table_number;
    if (!groupedByTable[k]) groupedByTable[k] = [];
    groupedByTable[k].push(o);
  });

  // Filter items by view
  const filterItems = (items) => {
    if (view === 'cuisine') return items.filter(i => i.type !== 'boisson');
    if (view === 'boissons') return items.filter(i => i.type === 'boisson');
    return items;
  };

  const statusColors = {
    'En attente': { bg: 'bg-red-500', border: 'border-red-400', text: 'text-red-700', light: 'bg-red-50', btn: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200' },
    'En préparation': { bg: 'bg-orange-500', border: 'border-orange-400', text: 'text-orange-700', light: 'bg-orange-50', btn: 'bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200' },
    'Prête': { bg: 'bg-green-500', border: 'border-green-400', text: 'text-green-700', light: 'bg-green-50', btn: 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200' },
    'Servie': { bg: 'bg-blue-500', border: 'border-blue-400', text: 'text-blue-700', light: 'bg-blue-50', btn: 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200' },
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white text-xl">Chargement...</div>;

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 text-white py-3 px-4 shadow-lg flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <Button onClick={() => navigate('/staff/dashboard')} variant="ghost" className="text-gray-300 hover:text-white hover:bg-gray-700 h-8"><ArrowLeft size={16} /></Button>
          <ChefHat size={24} className="text-orange-400" />
          <div>
            <h1 className="text-lg font-bold">Cuisine</h1>
            <p className="text-[10px] text-gray-400">{orders.length} commande(s) | {Object.keys(groupedByTable).length} table(s)</p>
          </div>
        </div>
        <div className="flex gap-1">
          {[{ id: 'cuisine', label: 'Plats', icon: UtensilsCrossed, color: 'bg-orange-600' },
            { id: 'boissons', label: 'Boissons', icon: Wine, color: 'bg-purple-600' },
            { id: 'all', label: 'Tout', icon: ChefHat, color: 'bg-gray-600' }].map(v => (
            <button key={v.id} onClick={() => setView(v.id)} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${view === v.id ? `${v.color} text-white` : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>
              <v.icon size={12} /> {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content - grouped by table */}
      <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {Object.entries(groupedByTable).map(([tableNum, tableOrders]) => {
          const hasVisibleItems = tableOrders.some(o => filterItems(o.items).length > 0);
          if (!hasVisibleItems) return null;
          return (
            <Card key={tableNum} className="bg-gray-800 border-gray-700 overflow-hidden" data-testid={`kitchen-table-${tableNum}`}>
              <CardHeader className="py-2 px-3 bg-gray-750 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                      <span className="text-xl font-black text-gray-900">{tableNum}</span>
                    </div>
                    <CardTitle className="text-white text-base">Table {tableNum}</CardTitle>
                  </div>
                  <span className="text-[10px] text-gray-400">{tableOrders.length} cmd</span>
                </div>
              </CardHeader>
              <CardContent className="p-2 space-y-2">
                {tableOrders.map(order => {
                  const visibleItems = filterItems(order.items);
                  if (!visibleItems.length) return null;
                  const sc = statusColors[order.status] || statusColors['En attente'];
                  const nextStatuses = getAvailableStatuses(order.status);
                  return (
                    <div key={order.id} className={`rounded-lg border-l-4 ${sc.border} bg-gray-750 p-2`} data-testid={`kitchen-order-${order.id}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-bold text-white">{order.customer_name || 'Client'}</span>
                          <Badge className={`text-[8px] h-4 ${sc.bg} text-white border-none`}>{order.status}</Badge>
                          {order.drinks_with_food && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-amber-500/30 text-amber-300 border border-amber-500/50" data-testid={`kitchen-drinks-badge-${order.id}`}><Beer size={8} /> Boissons avec plats</span>}
                        </div>
                        <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                          <Clock size={8} /> {new Date(order.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Cayenne' })}
                        </span>
                      </div>
                      {/* Items with per-item status */}
                      <div className="space-y-0.5">
                        {visibleItems.map((item, idx) => {
                          const realIdx = order.items.indexOf(item);
                          const iStatus = item.status || 'En attente';
                          const sc2 = statusColors[iStatus] || statusColors['En attente'];
                          const nextIdx = STATUSES.indexOf(iStatus) + 1;
                          const nextStatus = nextIdx < STATUSES.length ? STATUSES[nextIdx] : null;
                          const prevIdx = STATUSES.indexOf(iStatus) - 1;
                          const prevStatus = prevIdx >= 0 ? STATUSES[prevIdx] : null;
                          return (
                            <div key={idx} className={`rounded px-2 py-1.5 border-l-2 ${sc2.border} bg-gray-900/50`}>
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-bold text-white">{item.quantity}x {item.name}</span>
                                <Badge className={`text-[7px] h-3.5 ${sc2.bg} text-white border-none`}>{iStatus}</Badge>
                              </div>
                              {(item.cuisson || item.sauce || item.accompagnements?.length > 0) && (
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {item.cuisson && <span className="text-[9px] bg-red-900/50 text-red-300 px-1.5 py-0.5 rounded">{item.cuisson}</span>}
                                  {item.sauce && <span className="text-[9px] bg-amber-900/50 text-amber-300 px-1.5 py-0.5 rounded">{item.sauce}</span>}
                                  {item.accompagnements?.map((a, i) => <span key={i} className="text-[9px] bg-green-900/50 text-green-300 px-1.5 py-0.5 rounded">{a}</span>)}
                                </div>
                              )}
                              {item.notes && <p className="text-[9px] text-yellow-400 mt-0.5 italic">{item.notes}</p>}
                              <div className="flex gap-1 mt-1">
                                {prevStatus && <button onClick={() => handleItemStatusChange(order.id, realIdx, prevStatus)}
                                  className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-[8px] font-bold ${(statusColors[prevStatus] || {}).btn || ''}`}
                                  data-testid={`kitchen-item-prev-${order.id}-${realIdx}`}><Undo2 size={7} /> {prevStatus.split(' ').pop()}</button>}
                                {nextStatus && <button onClick={() => handleItemStatusChange(order.id, realIdx, nextStatus)}
                                  className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-[8px] font-bold ${(statusColors[nextStatus] || {}).btn || ''}`}
                                  data-testid={`kitchen-item-next-${order.id}-${realIdx}`}><ArrowRight size={7} /> {nextStatus.split(' ').pop()}</button>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          );
        })}
      </div>
      {Object.keys(groupedByTable).length === 0 && (
        <div className="flex flex-col items-center justify-center py-20">
          <ChefHat size={48} className="text-gray-600 mb-4" />
          <p className="text-lg text-gray-400 font-bold">Aucune commande en cours</p>
        </div>
      )}
    </div>
  );
};

export default KitchenDisplay;
